import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Phone, MessageSquareText } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "../../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SMS_HREF = `sms:${PHONE_TEL}?body=${encodeURIComponent("Hi Journey's Handyman, I'd like a quote for ")}`;

const CHIPS = [
  "Get a free estimate",
  "My deck needs repair",
  "Financing options?",
  "How soon can you come out?",
];

const GREETING = "Hey there! I'm Sam 👋 — ask me anything about painting, decks, remodels, roofing or emergency restoration. How can we help with your home?";

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "sam", text: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const sessionId = useRef(`web-${Math.random().toString(36).slice(2)}-${Date.now()}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy, open]);

  const send = async (text) => {
    const msg = text.trim();
    if (!msg || busy) return;
    const history = messages.map((m) => ({ role: m.role === "sam" ? "Sam" : "visitor", text: m.text }));
    setMessages((m) => [...m, { role: "visitor", text: msg }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/chat`, { session_id: sessionId.current, message: msg, history });
      setMessages((m) => [...m, { role: "sam", text: data.reply }]);
      if (data.lead_captured) setLeadCaptured(true);
    } catch (err) {
      setMessages((m) => [...m, { role: "sam", text: `Sorry, I'm having trouble right now. Please call us at ${PHONE_DISPLAY}.` }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="chat-open-button"
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-4 lg:bottom-6 lg:right-6 z-50 inline-flex items-center gap-2 bg-[#0E2A56] hover:bg-[#0A1E3F] text-white font-heading font-bold uppercase tracking-wide pl-4 pr-5 py-4 rounded-full shadow-hard-sm transition-colors"
          aria-label="Chat with Sam"
        >
          <MessageCircle className="h-6 w-6" strokeWidth={2.5} /> Chat with Sam
        </button>
      )}

      {open && (
        <div data-testid="chat-panel" className="fixed bottom-24 right-4 lg:bottom-6 lg:right-6 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[560px] bg-white border-4 border-[#0E2A56] flex flex-col shadow-hard">
          <div className="flex items-center justify-between bg-[#0E2A56] text-white px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-9 w-9 items-center justify-center bg-[#C8102E] rounded-full font-heading font-black">S</span>
              <div className="leading-none">
                <p className="font-heading font-bold uppercase tracking-wide">Sam</p>
                <p className="text-[11px] text-white/60">Journey's Handyman Assistant</p>
              </div>
            </div>
            <button data-testid="chat-close-button" onClick={() => setOpen(false)} aria-label="Close chat"><X className="h-6 w-6" /></button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-[#F6F5F0]">
            {messages.map((m, i) => (
              <div key={i} data-testid={`chat-msg-${m.role}`} className={`flex ${m.role === "visitor" ? "justify-end" : "justify-start"}`}>
                <span className={`inline-block max-w-[85%] px-3 py-2 text-sm leading-snug ${m.role === "visitor" ? "bg-[#0E2A56] text-white" : "bg-white border-2 border-[#0E2A56] text-[#14161A]"}`}>
                  {m.text}
                </span>
              </div>
            ))}
            {busy && <div className="flex justify-start"><span className="inline-block bg-white border-2 border-[#0E2A56] px-3 py-2 text-sm text-slate-500">Sam is typing…</span></div>}
          </div>

          {!leadCaptured && (
            <div data-testid="chat-chips" className="flex gap-2 overflow-x-auto px-3 py-2 border-t border-[#0E2A56]/10 bg-white">
              {CHIPS.map((c) => (
                <button key={c} data-testid={`chat-chip-${c.slice(0, 8)}`} onClick={() => send(c)} disabled={busy} className="whitespace-nowrap text-xs font-heading font-semibold uppercase tracking-wide border-2 border-[#0E2A56] text-[#0E2A56] px-3 py-1.5 hover:bg-[#0E2A56] hover:text-white transition-colors disabled:opacity-50">
                  {c}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="flex items-center gap-2 p-3 border-t-2 border-[#0E2A56] bg-white">
            <a href={SMS_HREF} data-testid="chat-text-us" className="shrink-0 inline-flex h-11 w-11 items-center justify-center bg-[#16a34a] text-white" aria-label="Text us">
              <MessageSquareText className="h-5 w-5" strokeWidth={2.5} />
            </a>
            <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type your message…" className="flex-1 h-11 px-3 border-2 border-[#0E2A56] text-[#14161A] focus:outline-none focus:border-[#C8102E]" />
            <button data-testid="chat-send" type="submit" disabled={busy} className="shrink-0 inline-flex h-11 w-11 items-center justify-center bg-[#C8102E] hover:bg-[#9E0C24] text-white disabled:opacity-50">
              <Send className="h-5 w-5" strokeWidth={2.5} />
            </button>
          </form>
        </div>
      )}
    </>
  );
};
